import Carousel from '@carvana/ds-carousel';
import Link from '@carvana/ds-link';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import TrackClick from '@carvana/merch-components/TrackClick';
import { NavPayloadAction } from '@carvana/merch-native';
import cx from 'classnames';
import { ChevronRight } from 'lucide-react';
import { useContext } from 'react';
import homeFeedBodyStyles from '~/constants/common/bodystylesList';
import { INTERACTION_TO_ACTION } from '~/constants/common/CarouselAnalytics';
import Sections from '~/constants/common/sections';
import { useIsMobile } from '~/context/common/DeviceContext';
import useGetConsumerHref from '~/hooks/common/selectors/useGetConsumerHref';
import { nativeSrpPayload } from '~/native/common/utils';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import optimizeImage from '~/utilities/optimizeImage';
import SectionWrapper from '../SectionWrapper';

type OwnProps = {
  hasBackground?: boolean;
  hasTopSpacing?: boolean;
  whiteBg?: boolean;
};

function PopularBodyStyles({
  hasBackground = true,
  hasTopSpacing = false,
  whiteBg = false
}: OwnProps) {
  const { trackEvent } = useContext(AnalyticsContext);
  const getHref = useGetConsumerHref();
  const isMobile = useIsMobile();

  return (
    <SectionWrapper
      className="flex flex-col gap-y-sm md:gap-y-2xl lg:px-4xl"
      analyticsSection="Popular Body Styles"
      eventName="Content Section"
      hasBackground={hasBackground}
      hasTopSpacing={hasTopSpacing}
      hasBottomSpacing
    >
      <Carousel
        ariaLabel="Popular Body Styles"
        data-testid="popular-body-styles-carousel"
        classes={{
          slide:
            'pl-md first:pl-2xl last:pr-2xl sm:pl-2xl sm:first:pl-4xl sm:last:pr-4xl md:first:pl-3xl md:last:pr-3xl lg:pl-0 lg:first:pl-0 lg:last:pr-0 pb-[2px]',
          slides: 'gap-0 lg:gap-xl',
          topSection:
            'px-2xl sm:px-4xl md:px-3xl gap-0 lg:px-0 sm:grid-cols-[1fr_auto]'
        }}
        hidePagination={!isMobile}
        headerContent={
          <h2 className="text-text-strong t-heading-lg sm:mb-sm md:t-heading-xl">
            Popular vehicle styles
          </h2>
        }
        hideScrollBar
        slideByVisible
        trackEvent={event => {
          trackEvent('Popular Body Styles', {
            ...event,
            action: INTERACTION_TO_ACTION[event.interactionType]
          });
        }}
      >
        {homeFeedBodyStyles.map(bodyStyle => (
          <TrackClick
            key={bodyStyle.link}
            element="a"
            href={getHref(bodyStyle.link)}
            className={cx(
              'group/tile flex min-w-[134px] flex-col gap-y-sm p-lg md:min-w-[168px]',
              { 'rounded-lg bg-background-default': whiteBg }
            )}
            data-analytics-id="Home feed card"
            data-analytics-attributes={{
              description: 'Popular body style tile',
              content: bodyStyle.alt
            }}
            data-testid="popular-body-style"
            data-native-payload={{
              action: NavPayloadAction.ViewSrp
            }}
          >
            <p className="text-center text-text-strong transition-colors t-body-sm group-hover/tile:text-text-strong-active group-hover/tile:t-strong-sm">
              {bodyStyle.alt}
            </p>

            <img
              src={optimizeImage(bodyStyle.image, { width: 150 })}
              alt=""
              className="mx-auto w-[110px] transition-transform group-hover/tile:scale-110 md:box-content md:w-[124px] md:px-md md:pb-md"
              loading="lazy"
            />
          </TrackClick>
        ))}

        <Link
          href={getHref('/cars')}
          aria-label="Shop All body styles"
          className="flex h-full w-[124px] items-center justify-center gap-x-sm md:w-[168px] md:gap-x-md"
          variant="standalone"
          preventVisitedColor
          data-analytics-id="Home feed card"
          data-analytics-attributes={JSON.stringify({
            description: 'Popular body style tile',
            content: 'Shop all'
          })}
          data-testid="shop-all-body-styles"
          data-native-payload={nativeSrpPayload}
        >
          Shop All
          <ChevronRight size={24} />
        </Link>
      </Carousel>
    </SectionWrapper>
  );
}

export default withErrorBoundary(PopularBodyStyles, {
  section: Sections.PopularBodyStyles
});
